/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, IconButton, Modal, Skeleton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import CloseIcon from "@mui/icons-material/Close";
import tol from "@assets/img/tooltip.svg";
import { InactivityHandler } from "@components/InactivityHandler";
import ButtonExpedienteForm from "@components/ButtonExpedienteForm";
import ButtonValidacionForm from "@components/ButtonValidacionForm";
import { BOFU_TABS_CONFIG } from "@helpers/constantes";
import CustomRightImage from "@components/CustomRightImagev2";
import TabBofu from "@components/TabBofu/TabBofu";
import DocumentsTabBofu from "@components/TabBofu/DocumentsTabBofu";
import {
  setAvalAdicionalIdInformation,
  setAvalAdicionalValidationId,
  setAvalAdicionalIdBankInformation,
  setBofuTabAvalDocument,
  setBofuTabAvalAdicionalDocument,
} from "@store/slices/bofuSlice";
import ButtonContinue from "@components/TabBofu/ButtonContinue";
import { MATRIMONIAL_REGIMEN_CODES } from "@helpers/constantes";
import { CustomCompanyName } from "@components/CustomCompanyName";
import { PersonaStructure } from "@interfaces/index";

const DocsAval = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const dispatch = useAppDispatch();

  const bufuLoading = useSelector((state: RootState) => state.bofu.isLoading);
  const personAval = useSelector((state: RootState) => state.bofu.aval);
  const personConyugeAval = useSelector((state: RootState) => state.bofu.aval_conyuge);
  const avalesAdicionales = useSelector((state: RootState) => state.bofu.avales_adicionales);
  const representantes = useSelector((state: RootState) => state.bofu.bofu_tabs_obj.representantes.items);
  const regimenMatrimonialAval = useSelector((state: RootState) => state.bofu.aval_regimen_conyugal);
  const bofuTabs = useSelector((state: RootState) => state.bofu.bofu_tabs_obj);
  const showInformationFormCoyuge = useSelector(
    (state: RootState) => state.bofu.bofu_tabs_obj.aval.show_information_form_conyuge,
  );

  const [open, setOpen] = useState(false);
  const [showIdentityValidationAval, setShowIdentityValidationAval] = useState<boolean>(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleClickAvalAdicionalInformation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/informacion_aval_adicional");
    }, 200);
  };

  const handleClickAvalAdicionalSendInformation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/aval_adicional_enviar");
    }, 200);
  };

  const handleClickAvalAdicionalValidation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalValidationId(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/validacion_identidad_aval_adicional");
    }, 200);
  };

  const handleClickAvalAdicionalBankInformation = (avalAdicional: PersonaStructure) => {
    dispatch(setAvalAdicionalIdBankInformation(avalAdicional.id));
    setTimeout(() => {
      navigate("/producto/creditosimple/info_bancaria_aval_adicional");
    }, 200);
  };

  useEffect(() => {
    (async () => {
      if (!personAval) {
        navigate("/producto/creditosimple/docs_empresa");
        return;
      }

      const representantes_ids = representantes.map((representante) => representante.persona.id);
      const is_same_person = representantes_ids.includes(personAval?.id);
      setShowIdentityValidationAval(is_same_person ? false : true);
    })();
  }, []);

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8} mt={2}>
          <Box textAlign={"center"} mx={4}>
            <InactivityHandler />

            <CustomCompanyName />

            <TabBofu />

            {bufuLoading ? (
              <Box mt={3}>
                <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
              </Box>
            ) : (
              <Box mt={4}>
                {bofuTabs?.aval?.show_form_aval && (
                  <Box>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={700}
                      color={theme?.palette?.primary?.dark}
                    >
                      Ahora es momento de llenar los formatos relacionados al aval{" "}
                      <img src={tol} alt="" onClick={handleOpen} />
                    </Typography>

                    <Typography
                      mt={2}
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      color={theme?.palette?.primary?.main}
                    >
                      {`${personAval?.name || ""} ${personAval?.last_name || ""} ${personAval?.last_name_2 || ""}`}
                    </Typography>

                    <Box
                      mt={2}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      color={theme?.palette?.primary?.dark}
                    >
                      <Typography variant={isMobileOrTablet ? "body2" : "body1"} sx={{ flex: 1, textAlign: "left" }}>
                        Información del aval
                      </Typography>

                      <ButtonExpedienteForm
                        formStatus={personAval?.personal_information_status || null}
                        onClick={() => navigate("/producto/creditosimple/informacion_datos_aval")}
                      />
                    </Box>

                    {showIdentityValidationAval && (
                      <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          sx={{ flex: 1, textAlign: "left" }}
                          color={theme?.palette?.primary?.dark}
                        >
                          Validación de identidad
                        </Typography>

                        <ButtonValidacionForm
                          isCompleted={personAval?.identity_validation_completed}
                          documentStatus={bofuTabs.aval.doc_validacion_identidad?.validated}
                          onClick={() =>
                            navigate(`/producto/creditosimple/validacion_identidad_aval/${personAval?.id}`)
                          }
                        />
                      </Box>
                    )}

                    <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        sx={{ flex: 1, textAlign: "left" }}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información bancaria
                      </Typography>

                      <ButtonExpedienteForm
                        formStatus={personAval?.bank_references_status || null}
                        onClick={() => navigate("/producto/creditosimple/info_bancaria_aval")}
                      />
                    </Box>

                    {bofuTabs?.aval?.documents.length > 0 && (
                      <Box>
                        <Typography
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          fontWeight={700}
                          color={theme?.palette?.primary?.dark}
                          mt={4}
                        >
                          Ahora carga los documentos relacionados al aval
                        </Typography>

                        <DocumentsTabBofu
                          inputPrefix="aval"
                          tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                          documents={bofuTabs?.aval?.documents || []}
                          onChange={({ document, newValue }) => {
                            dispatch(
                              setBofuTabAvalDocument({
                                document,
                                newValue,
                              }),
                            );
                          }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {personConyugeAval && (
                  <Box mt={3}>
                    <Box>
                      <Typography
                        variant={isMobileOrTablet ? "body2" : "body1"}
                        fontWeight={700}
                        color={theme?.palette?.primary?.dark}
                      >
                        Información del Conyuge del Aval
                      </Typography>

                      {showInformationFormCoyuge && (
                        <Box
                          mt={2}
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          color={theme?.palette?.primary?.dark}
                        >
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                          >
                            Datos generales
                          </Typography>

                          <ButtonExpedienteForm
                            formStatus={personConyugeAval?.personal_information_status}
                            onClick={() => navigate("/producto/creditosimple/informacion_conyuge_aval")}
                          />
                        </Box>
                      )}

                      {regimenMatrimonialAval === MATRIMONIAL_REGIMEN_CODES.sociedad_conyugal.code && (
                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Validación de identidad
                          </Typography>

                          <ButtonValidacionForm
                            isCompleted={personConyugeAval?.identity_validation_completed}
                            documentStatus={bofuTabs.aval.doc_validacion_identidad_conyuge?.validated}
                            onClick={() =>
                              navigate(`/producto/creditosimple/validacion_identidad_conyugue/${personConyugeAval?.id}`)
                            }
                          />
                        </Box>
                      )}
                    </Box>

                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={700}
                      color={theme?.palette?.primary?.dark}
                      mt={5}
                      mb={4}
                    >
                      Ahora carga los documentos relacionados al conyuge
                    </Typography>

                    <DocumentsTabBofu
                      inputPrefix="conyuge_aval"
                      tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                      documents={bofuTabs?.aval?.documents_conyuge || []}
                      onChange={({ document, newValue }) => {
                        dispatch(
                          setBofuTabAvalDocument({
                            document,
                            newValue,
                          }),
                        );
                      }}
                    />
                  </Box>
                )}

                {avalesAdicionales.length > 0 && (
                  <Box mt={3}>
                    <Typography
                      variant={isMobileOrTablet ? "body2" : "body1"}
                      fontWeight={700}
                      color={theme?.palette?.primary?.dark}
                    >
                      Ahora carga los documentos relacionados al aval adicional
                    </Typography>

                    {avalesAdicionales.map((avalAdicional, index) => (
                      <Box key={avalAdicional.persona.id} mt={2}>
                        <Typography
                          mt={2}
                          variant={isMobileOrTablet ? "body2" : "body1"}
                          color={theme?.palette?.primary?.main}
                        >
                          {`${avalAdicional.persona.name || ""} ${avalAdicional.persona.last_name || ""} ${
                            avalAdicional.persona.last_name_2 || ""
                          }`}
                        </Typography>

                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Información aval adicional
                          </Typography>

                          <ButtonExpedienteForm
                            formStatus={null}
                            onClick={() => handleClickAvalAdicionalSendInformation(avalAdicional.persona)}
                          />
                        </Box>

                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Información del aval
                          </Typography>

                          <ButtonExpedienteForm
                            formStatus={avalAdicional.persona?.personal_information_status || null}
                            onClick={() => handleClickAvalAdicionalInformation(avalAdicional.persona)}
                          />
                        </Box>

                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Validación de identidad
                          </Typography>

                          <ButtonValidacionForm
                            isCompleted={avalAdicional.persona.identity_validation_completed}
                            documentStatus={avalAdicional.doc_validacion_identidad?.validated}
                            onClick={() => handleClickAvalAdicionalValidation(avalAdicional.persona)}
                          />
                        </Box>

                        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
                          <Typography
                            variant={isMobileOrTablet ? "body2" : "body1"}
                            sx={{ flex: 1, textAlign: "left" }}
                            color={theme?.palette?.primary?.dark}
                          >
                            Información bancaria
                          </Typography>

                          <ButtonExpedienteForm
                            formStatus={avalAdicional.persona.bank_references_status || null}
                            onClick={() => handleClickAvalAdicionalBankInformation(avalAdicional.persona)}
                          />
                        </Box>

                        <Box mt={2}>
                          <DocumentsTabBofu
                            inputPrefix={`aval_adicional_${index}`}
                            tabCode={BOFU_TABS_CONFIG.tab_aval.code}
                            documents={avalAdicional.documents}
                            onChange={({ document, newValue }) => {
                              dispatch(
                                setBofuTabAvalAdicionalDocument({
                                  document,
                                  newValue,
                                  persona: avalAdicional.persona,
                                  valueAction: undefined,
                                  valueIndex: undefined,
                                }),
                              );
                            }}
                            onChangeMultiple={({ document, newValue, valueAction, valueIndex }) => {
                              dispatch(
                                setBofuTabAvalAdicionalDocument({
                                  document,
                                  newValue,
                                  persona: avalAdicional.persona,
                                  valueAction: valueAction || undefined,
                                  valueIndex,
                                }),
                              );
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                <Box mt={4}>
                  <ButtonContinue tabCode={BOFU_TABS_CONFIG.tab_aval.code} />
                </Box>
              </Box>
            )}
          </Box>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>

      <Modal open={open} onClose={handleClose} aria-labelledby="modal-title" aria-describedby="modal-description">
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "250px", sm: "300px", md: "60vw", lg: "40vw" },
            maxWidth: 500,
            bgcolor: "background.paper",
            border: "2px solid #002652",
            borderRadius: "10px",
            boxShadow: 24,
            p: 4,
            textAlign: "center",
          }}
        >
          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              color: "#76A1FF",
            }}
          >
            <CloseIcon />
          </IconButton>

          <Typography
            id="modal-title"
            variant="h6"
            component="h2"
            fontWeight={600}
            color={theme?.palette?.primary?.dark}
          >
            Aval
          </Typography>

          <Typography id="modal-description" sx={{ mt: 2, color: theme?.palette?.primary?.dark }}>
            Es el accionista mayoritario de la empresa, que a su vez, será la persona encargada de asegurar que se
            saldará la deuda.
          </Typography>
        </Box>
      </Modal>
    </>
  );
};

export default DocsAval;
